import React, { useEffect, useId, useState } from 'react'
import { ConfirmDialog } from '../../../../shared/sso-workspace/confirm-dialog'
import { dateTimeText, timeText } from '../../../../shared/date/local-time'
import { ErrorReference } from '../../../../shared/error/error-reference'
import { serverMessage } from '../../../../shared/error/server-message'
import { useErrorLine } from '../../../../shared/error/use-error-line'
import { plural } from '../../../../shared/sso-workspace/format'
import { aiToolsAvailable, markFieldsOf, putMarkFilter } from './ai-tools-api'
import { KINDS } from './ai-tools-download'
import { MarkFields, useMarkFields } from './mark-dialog'
import { isZip, readZipText } from './ai-tools-zip'
import { EXPORT_JSON } from './ai-tools-folder'
import { consumerProblem, filterWords, isTooMany, kindItems, kindNoun, matchFilter, tooManyText } from './ai-tools-words'
import '../../org/api-keys/api-keys.scss'

// The marks that cover many rows at once (with SYS_AI_TOOLS_TELEMETRY update),
// through PUT /processing's filter form: one "Processed by" name and status
// applied to every row in scope. Three flows (owner 2026-10-29: marking what
// a download holds is its own workflow, not mixed with the table's), by mode:
//   matching  "Mark all matching" (the list's ⋮): every row the tab's filters
//             (and search) match as the dialog opens -- upTo is that moment,
//             so nothing received while it is open is marked unseen;
//   selected  "Mark selected" (select mode's "all matching except these":
//             { filter, except, count }; any kind, a session's records and
//             findings too): the selection, its except sent along;
//   download  "Mark a downloaded file" (Sessions, Postmortems): what a file
//             made by Download holds, read from its export.json (the .zip
//             stores it uncompressed; export.json itself, or the first line
//             of an older single-file .jsonl, also works), with its own
//             filter and upTo, so rows that changed since are skipped. The
//             download this tab just made is offered without choosing a file.
// A dry run says "This marks N sessions" (or "clears N marks") before
// anything is written, and the button says the same; 413 too_many_matches
// says the 10,000 limit and holds the button.
//
// kind: 'session' | 'postmortem' (download), any kind (matching, selected);
// filters: the tab's list filters; last: the tab's last completed download
// { filter, upTo, count, at } or null; onDone(answer): the marks were written.

const HEADER_BYTES = 65536
const NOT_A_DOWNLOAD = "That doesn't look like a file made by Download."

const readText = blob => (typeof blob.text === 'function'
  ? blob.text()
  : new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(reader.error)
    reader.readAsText(blob)
  }))

// A file's header (its first line), checked against the tab: { header } or { problem }.
const readBytes = blob => (typeof blob.arrayBuffer === 'function'
  ? blob.arrayBuffer().then(buffer => new Uint8Array(buffer))
  : new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(new Uint8Array(reader.result))
    reader.onerror = () => reject(reader.error)
    reader.readAsArrayBuffer(blob)
  }))

// The header's text: a zip's telemetry/export.json, else the file's first line.
const headerText = async file => {
  const start = await readBytes(file.slice(0, 4))
  if (isZip(start)) return readZipText(await readBytes(file), EXPORT_JSON)
  return readText(file.slice(0, HEADER_BYTES))
}

export const readHeader = async (file, kind) => {
  const spec = KINDS[kind]
  let header
  try {
    const text = await headerText(file)
    if (text === null) return { problem: NOT_A_DOWNLOAD }
    const end = text.indexOf('\n')
    header = JSON.parse(end === -1 ? text : text.slice(0, end))
  } catch (err) {
    return { problem: NOT_A_DOWNLOAD }
  }
  if (!header || typeof header !== 'object' || typeof header.export !== 'string') return { problem: NOT_A_DOWNLOAD }
  if (header.export !== spec.header) return { problem: `This file holds ${header.export.replace(/^ai-tools-/, '')}, not ${spec.items}.` }
  if (header.version !== 1) return { problem: 'This file was made by a different version of Download, which this page cannot read.' }
  if (!header.filter || typeof header.filter !== 'object' || header.filter.kind !== kind || typeof header.upTo !== 'string' || !Number.isFinite(Date.parse(header.upTo))) return { problem: NOT_A_DOWNLOAD }
  return { header }
}

const fileWords = header => `Downloaded ${dateTimeText(header.exportedAt)}: ${Number.isFinite(header.count) ? plural(header.count, 'item') : 'an unknown number of items'}, filters ${filterWords(header.filter)}.`

export const MarkMatchingDialog = ({ kind, mode: asked, filters, last, consumers, selected, onCancel, onDone }) => {
  const mode = selected ? 'selected' : asked || 'matching'
  const spec = { noun: kindNoun(kind), items: kindItems(kind) }
  const ids = { scope: useId(), file: useId() }
  const fields = useMarkFields(consumers)
  const [openedAt] = useState(() => new Date().toISOString())
  const now = { ...matchFilter(kind, filters), upTo: openedAt }
  const except = selected ? selected.except || [] : undefined
  const offerDownload = !!last
  const [scope, setScope] = useState(() => (offerDownload ? 'download' : 'file'))
  const [file, setFile] = useState({ header: null, problem: null })
  const [dry, setDry] = useState({ state: 'idle', text: '' })
  const [busy, setBusy] = useState(false)
  const [error, failure, setError] = useErrorLine()

  // a download that is no longer offered (the filters changed) is not the
  // scope: the file is asked for instead, never "now" by itself
  const effective = mode === 'selected' ? 'selected' : mode === 'matching' ? 'now' : scope === 'download' && !offerDownload ? 'file' : scope
  const filter = effective === 'selected' ? selected.filter
    : effective === 'now' ? now
    : effective === 'download' ? { ...last.filter, upTo: last.upTo }
      : file.header ? { ...file.header.filter, upTo: file.header.upTo } : null
  const { consumer, status } = fields
  const key = filter && !consumerProblem(consumer) ? JSON.stringify([filter, except, consumer, status]) : ''

  // The count, whenever the scope, the filters or the consumer and status change.
  useEffect(() => {
    if (!key || !aiToolsAvailable()) {
      setDry({ state: 'idle', text: '' })
      return undefined
    }
    let stale = false
    setDry({ state: 'loading', text: '' })
    putMarkFilter(filter, { consumer, status }, { dryRun: true, ...(selected ? { except } : {}) }).then(
      answer => {
        if (stale) return
        const a = answer || {}
        const n = status === 'unprocessed' ? a.cleared || 0 : a.marked || 0
        setDry({ state: 'ok', count: n, text: status === 'unprocessed' ? `This clears ${plural(n, 'mark')}.` : `This marks ${plural(n, spec.noun, spec.items)}.` })
      },
      err => { if (!stale) setDry({ state: 'bad', text: isTooMany(err) ? tooManyText(kind) : serverMessage(err, "The count couldn't be read."), failure: err }) }
    )
    return () => { stale = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  const chooseFile = async e => {
    const chosen = e.target.files && e.target.files[0]
    setError(null)
    if (!chosen) return setFile({ header: null, problem: null })
    setFile(await readHeader(chosen, kind).then(r => ({ header: r.header || null, problem: r.problem || null })))
  }

  const confirm = async () => {
    if (busy || !filter) return
    if (!aiToolsAvailable()) return setError("AI tools telemetry isn't available in this build, so nothing was marked.")
    const problem = fields.problem()
    if (problem) return setError(problem)
    setBusy(true)
    setError(null)
    let answer
    try {
      const mark = markFieldsOf({ consumer, status, note: fields.note })
      answer = await (selected ? putMarkFilter(filter, mark, { except }) : putMarkFilter(filter, mark))
    } catch (err) {
      setBusy(false)
      setError(isTooMany(err) ? tooManyText(kind) : serverMessage(err, "The marks couldn't be saved."), err)
      return
    }
    setBusy(false)
    onDone(answer || {})
  }

  const choose = value => { setError(null); setScope(value) }
  const sources = offerDownload
    ? [
        { value: 'download', label: `The download you made at ${timeText(last.at, { zone: false })}`, words: `${plural(last.count, 'item')}, as they were up to ${dateTimeText(last.upTo)}.` },
        { value: 'file', label: 'A file you downloaded earlier', words: '' }
      ]
    : []
  const blocked = !filter || dry.state !== 'ok'
  const selectedWords = selected && (Number.isFinite(selected.count)
    ? `The ${plural(selected.count, spec.noun, spec.items)} you selected.`
    : except.length ? `Every ${spec.noun} that matches, except the ${plural(except.length, spec.noun, spec.items)} you unticked.` : `Every ${spec.noun} that matches.`)
  const clearing = status === 'unprocessed'
  const count = dry.state === 'ok' ? dry.count : null
  const confirmText = count === null ? (clearing ? 'Clear the marks' : 'Mark')
    : clearing ? `Clear ${plural(count, 'mark')}` : `Mark ${plural(count, spec.noun, spec.items)}`
  const title = { selected: `Mark the selected ${spec.items}`, matching: `Mark all matching ${spec.items}`, download: 'Mark the sessions in a downloaded file' }[mode]
  const body = {
    selected: `Records who processed the ${spec.items} you selected, and how.`,
    matching: `Records who processed every ${spec.noun} these filters match now, and how. ${spec.items[0].toUpperCase() + spec.items.slice(1)} received after you opened this aren't marked.`,
    download: `Records who processed the ${spec.items} in a file you made with Download, and how. Only what the file holds is marked; anything that changed since you downloaded it is skipped.`
  }[mode]

  return (
    <ConfirmDialog
      open
      title={title}
      body={body}
      confirmText={confirmText}
      confirmClassName="btn sw-btn-primary"
      busy={busy}
      confirmDisabled={blocked}
      error={error}
      failure={failure}
      onConfirm={confirm}
      onCancel={onCancel}>
      {mode === 'selected' && <p className="mb-3">{selectedWords} <span className="sw-hint small d-block">{`Filters: ${filterWords(selected.filter)}.`}</span></p>}
      {mode === 'matching' && <p className="ait-scope mb-3"><span className="sw-hint small d-block">{`Filters: ${filterWords(now)}.`}</span></p>}
      {mode === 'download' &&
        <div className="mb-3">
          {sources.length > 0 &&
            <div className="ak-choices mb-2" role="radiogroup" aria-labelledby={ids.scope}>
              <div className="sw-label" id={ids.scope}>Which download</div>
              {sources.map(option =>
                <label key={option.value}>
                  <input type="radio" name={ids.scope} checked={effective === option.value} onChange={() => choose(option.value)} />
                  {option.label}
                  {effective === option.value && option.words && <span className="sw-hint small d-block">{option.words}</span>}
                </label>)}
            </div>}
          {effective === 'file' &&
            <div className="ait-file">
              <label className="sw-label" htmlFor={ids.file}>The file you downloaded</label>
              <input id={ids.file} className="form-control" type="file" accept=".zip,.json,.jsonl,.ndjson,application/zip,application/json,application/x-ndjson" onChange={chooseFile} />
              <span className="sw-hint small d-block mt-1">{`The .zip that Download saved (ai-tools-telemetry-${KINDS[kind].file}-….zip), or the export.json inside it.`}</span>
              {file.problem && <p className="text-danger small mt-1" role="alert">{file.problem}</p>}
              {file.header && <p className="ait-file-summary mt-2" role="status">{fileWords(file.header)}</p>}
            </div>}
        </div>}
      <MarkFields fields={fields} onEdit={() => setError(null)} />
      {dry.state === 'loading' && <p className="sw-hint" role="status">Counting…</p>}
      {dry.state === 'ok' && <p className="fw-semibold" role="status">{dry.text}</p>}
      {dry.state === 'bad' && <p className="text-danger small" role="alert">{dry.text} <ErrorReference error={dry.failure} /></p>}
    </ConfirmDialog>
  )
}
